import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Switch,
  Tabs,
  Text,
} from '@mantine/core';
import { t } from 'i18next';
import { PluginAccordionItem } from '../../../setup/molecules/plugin-accordion-item';
import { AllPluginsCheckbox } from '../../../setup/molecules/all-plugins-checkbox';
import { useReevaluateModal } from './use-reevaluate-modal';

interface ReevaluateModalProps {
  opened: boolean;
  onClose: () => void;
}

// The lists take the height the rest of the dialog leaves, so the whole
// dialog always fits on screen and the Run button stays visible.
const LIST_HEIGHT = 'calc(100vh - 480px)';
const LIST_MIN_HEIGHT = 200;

export const ReevaluateModal = ({ opened, onClose }: ReevaluateModalProps) => {
  const {
    fields,
    selected,
    toggleField,
    selectEmpty,
    selectFilled,
    selectAll,
    selectNone,
    allSelected,
    someSelected,
    toggleAll,
    redetectIterators,
    setRedetectIterators,
    plugins,
    togglePlugin,
    setAllPlugins,
    setPluginSetting,
    canRun,
    run,
  } = useReevaluateModal(opened, onClose);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('reevaluate.title')}
      size="80%"
      centered
    >
      <Stack spacing="md">
        <Text size="sm" color="dimmed">
          {t('reevaluate.description')}
        </Text>

        <Switch
          checked={redetectIterators}
          onChange={(e) => setRedetectIterators(e.currentTarget.checked)}
          label={t('reevaluate.redetectIterators')}
          description={t('reevaluate.redetectIteratorsHint')}
        />

        <Tabs defaultValue="fields">
          <Tabs.List>
            <Tabs.Tab value="fields">{t('reevaluate.fieldsTab')}</Tabs.Tab>
            <Tabs.Tab value="plugins">{t('reevaluate.pluginsTab')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="fields" pt="sm">
            <Group position="apart" mb="xs" noWrap>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAll}
                aria-label={t('reevaluate.selectAll')}
              />
              <Group spacing="xs" noWrap>
                <Button size="xs" variant="default" onClick={selectEmpty}>
                  {t('reevaluate.selectEmpty')}
                </Button>
                <Button size="xs" variant="default" onClick={selectFilled}>
                  {t('reevaluate.selectFilled')}
                </Button>
                <Button size="xs" variant="default" onClick={selectAll}>
                  {t('reevaluate.selectAll')}
                </Button>
                <Button size="xs" variant="default" onClick={selectNone}>
                  {t('reevaluate.selectNone')}
                </Button>
              </Group>
            </Group>
            <ScrollArea
              h={LIST_HEIGHT}
              mih={LIST_MIN_HEIGHT}
              type="auto"
              offsetScrollbars
            >
              <Stack spacing={6}>
                {fields.map((field) => (
                  <Checkbox
                    key={field.id}
                    checked={selected.has(field.id)}
                    onChange={() => toggleField(field.id)}
                    label={
                      <Group spacing={6} noWrap>
                        <Text size="sm">{field.name}</Text>
                        {field.stale && (
                          <Badge size="xs" color="orange">
                            {t('reevaluate.stale')}
                          </Badge>
                        )}
                        {field.filled && (
                          <Badge size="xs" color="gray">
                            {t('reevaluate.filled')}
                          </Badge>
                        )}
                      </Group>
                    }
                  />
                ))}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>

          <Tabs.Panel value="plugins" pt="sm">
            <AllPluginsCheckbox plugins={plugins} onChange={setAllPlugins} />
            <ScrollArea
              h={LIST_HEIGHT}
              mih={LIST_MIN_HEIGHT}
              type="auto"
              offsetScrollbars
            >
              <Accordion
                multiple
                chevron={null}
                styles={{ content: { padding: 0 } }}
              >
                {plugins.map((plugin) => (
                  <PluginAccordionItem
                    key={plugin.key}
                    plugin={plugin}
                    checked={plugin.enabled}
                    onToggle={() => togglePlugin(plugin.key)}
                    onSettingChange={setPluginSetting}
                  />
                ))}
              </Accordion>
            </ScrollArea>
          </Tabs.Panel>
        </Tabs>

        <Group position="right">
          <Button variant="default" onClick={onClose}>
            {t('reevaluate.cancel')}
          </Button>
          <Button disabled={!canRun} onClick={run}>
            {t('reevaluate.run', { count: selected.size })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
